import { logger } from '@/utils/logger/logger';
import {
  presentationAtom,
  presentationHistoryAtom,
  presentationOriginalAtom,
  type PresentationData,
  type SlideData,
} from '@heidi/agents/data';
import type { PptxExportChunk, SlideUpdateChunk } from '@heidi/evidence/contracts';
import { getDefaultStore } from 'jotai';
import { presentationPreviewAtom } from '../presentationPreviewAtom';
import { fetchPresentation } from './presentationApi';

// --- Batch history: collapse rapid slide_update chunks into a single undo step ---
let batchSnapshot: PresentationData | null = null;
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_DEBOUNCE_MS = 1500;

function finalizeBatch(): void {
  if (!batchSnapshot) return;
  const store = getDefaultStore();
  const history = store.get(presentationHistoryAtom);
  store.set(presentationHistoryAtom, {
    past: [...history.past, batchSnapshot].slice(-50),
    future: [],
  });
  batchSnapshot = null;
  batchTimer = null;
}

/**
 * Merge a slide_update chunk into an existing presentation and write the
 * result to the Jotai store.  Extracted so it can be reused by both the
 * fast-path (atom already populated) and the fetch-then-merge path.
 */
function mergeChunkIntoPresentation(
  presentation: PresentationData,
  chunk: SlideUpdateChunk,
  newSlide: SlideData
): void {
  const store = getDefaultStore();

  if (!batchSnapshot) {
    batchSnapshot = presentation;
  }

  const existingIndex = presentation.slides.findIndex((s) => s.id === chunk.slide_id);

  let updatedSlides: SlideData[];
  if (existingIndex >= 0) {
    updatedSlides = presentation.slides.map((slide) => {
      if (slide.id !== chunk.slide_id) return slide;
      return {
        ...slide,
        imageUrl: chunk.image_url || slide.imageUrl,
        title: chunk.title ?? slide.title,
      };
    });
  } else {
    const insertIndex = newSlide.index;
    const shifted = presentation.slides.map((s) => ({
      ...s,
      index: s.index >= insertIndex ? s.index + 1 : s.index,
    }));
    updatedSlides = [...shifted, newSlide].sort((a, b) => a.index - b.index);
  }

  if (chunk.updated_reference_slides?.length) {
    const refMap = new Map(chunk.updated_reference_slides.map((r) => [r.slide_id, r]));
    updatedSlides = updatedSlides.map((slide) => {
      const ref = refMap.get(slide.id);
      if (!ref?.image_url) return slide;
      return { ...slide, imageUrl: ref.image_url, title: ref.title ?? slide.title };
    });
  }

  const updated: PresentationData = {
    ...presentation,
    slides: updatedSlides,
    ...(chunk.pptx_url ? { pptxUrl: chunk.pptx_url } : {}),
  };

  if (batchTimer) clearTimeout(batchTimer);
  batchTimer = setTimeout(finalizeBatch, BATCH_DEBOUNCE_MS);

  store.set(presentationAtom, updated);
  logger.info('[SlideBuilder] Updated presentation slides', {
    slideCount: updatedSlides.length,
  });
}

/**
 * Handles a `slide_update` chunk from the evidence chat stream.
 *
 * If the presentation atom already holds the matching presentation the
 * new slide is merged in.  If the atom is empty the full presentation is
 * fetched from the API first so we never replace a complete deck with a
 * single-slide skeleton.  Chunks for a *different* presentation ID are
 * silently ignored.
 */
export async function handleSlideUpdateChunk(chunk: SlideUpdateChunk): Promise<void> {
  logger.info('[SlideBuilder] slide_update chunk received', {
    status: chunk.status,
    presentationId: chunk.presentation_id,
    slideId: chunk.slide_id,
    slideIndex: chunk.slide_index,
    hasImageUrl: !!chunk.image_url,
  });

  if (chunk.status === 'render_failed') {
    // Content persisted but the preview PNG did not re-render. Keep the
    // existing image (stale rather than blank) and only merge metadata; the
    // assistant's reply tells the user the preview may be out of date.
    logger.warn('[SlideBuilder] slide_update saved without a refreshed preview', {
      presentationId: chunk.presentation_id,
      slideId: chunk.slide_id,
    });
    const current = getDefaultStore().get(presentationAtom);
    if (current && current.id === chunk.presentation_id) {
      mergeChunkIntoPresentation(current, chunk, {
        id: chunk.slide_id,
        index: chunk.slide_index ?? 0,
        title: chunk.title ?? '',
      });
    }
    return;
  }

  if (chunk.status !== 'completed') return;

  const store = getDefaultStore();
  const presentation = store.get(presentationAtom);

  const newSlide: SlideData = {
    id: chunk.slide_id,
    index: chunk.slide_index ?? 0,
    title: chunk.title ?? '',
    imageUrl: chunk.image_url || undefined,
  };

  if (presentation && presentation.id === chunk.presentation_id) {
    mergeChunkIntoPresentation(presentation, chunk, newSlide);
    return;
  }

  if (presentation && presentation.id !== chunk.presentation_id) {
    logger.info('[SlideBuilder] Ignoring slide_update for different presentation', {
      atomId: presentation.id,
      chunkId: chunk.presentation_id,
    });
    return;
  }

  // Atom is null -- fetch the full presentation from the API so we don't
  // replace a complete deck with a single-slide skeleton.
  try {
    const fullData = await fetchPresentation(chunk.presentation_id);
    mergeChunkIntoPresentation(fullData, chunk, newSlide);
    store.set(presentationOriginalAtom, store.get(presentationAtom));
    logger.info('[SlideBuilder] Fetched full presentation before merging slide_update', {
      presentationId: chunk.presentation_id,
      slideCount: fullData.slides.length,
    });
  } catch (err) {
    logger.warn('[SlideBuilder] Failed to fetch presentation, falling back to skeleton', {
      presentationId: chunk.presentation_id,
      error: err,
    });
    const skeleton: PresentationData = {
      id: chunk.presentation_id,
      title: '',
      slides: [newSlide],
      chatId: null,
    };
    store.set(presentationAtom, skeleton);
    store.set(presentationOriginalAtom, skeleton);
  }
}

/**
 * Handles a `slide_delete` chunk from the evidence chat stream.
 * Removes the deleted slide from the presentation atom.
 */
export function handleSlideDeleteChunk(chunk: {
  presentation_id: string;
  slide_id: string;
  status: string;
}): void {
  if (chunk.status !== 'completed') return;

  const store = getDefaultStore();
  const presentation = store.get(presentationAtom);
  if (!presentation || presentation.id !== chunk.presentation_id) return;

  const history = store.get(presentationHistoryAtom);
  store.set(presentationHistoryAtom, {
    past: [...history.past, presentation].slice(-50),
    future: [],
  });

  const updatedSlides = presentation.slides
    .filter((s) => s.id !== chunk.slide_id)
    .map((s, i) => ({ ...s, index: i }));

  store.set(presentationAtom, { ...presentation, slides: updatedSlides });
  logger.info('[SlideBuilder] Deleted slide via stream', {
    slideId: chunk.slide_id,
    remainingSlides: updatedSlides.length,
  });
}

/**
 * Handles a `pptx_export` chunk to seed the presentation atom with metadata.
 * Called when the backend signals presentation generation.
 */
export function handlePptxExportChunk(chunk: PptxExportChunk): void {
  logger.info('[SlideBuilder] pptx_export chunk received', {
    status: chunk.status,
    presentationId: chunk.presentation_id,
    filename: chunk.filename,
    slideCount: chunk.slide_count,
    hasDownloadUrl: !!chunk.download_url,
    hasPreviewUrl: !!chunk.preview_url,
  });

  if (!chunk.presentation_id) {
    logger.warn(
      '[SlideBuilder] pptx_export chunk has no presentation_id — cannot seed atom'
    );
    return;
  }

  const store = getDefaultStore();
  const presentation = store.get(presentationAtom);

  if (presentation && presentation.id === chunk.presentation_id) {
    const updates: Partial<PresentationData> = {};
    if (chunk.filename && !presentation.title) {
      updates.title = chunk.filename.replace(/\.pptx$/i, '');
    }
    if (chunk.download_url) updates.pptxUrl = chunk.download_url;
    if (chunk.preview_url) updates.previewUrl = chunk.preview_url;

    if (Object.keys(updates).length > 0) {
      store.set(presentationAtom, { ...presentation, ...updates });
    }

    if (chunk.status === 'completed') {
      store.set(presentationPreviewAtom, {
        isOpen: true,
        presentationId: chunk.presentation_id,
      });
    }
    return;
  }

  const skeleton: PresentationData = {
    id: chunk.presentation_id,
    title: chunk.filename?.replace(/\.pptx$/i, '') ?? '',
    slides: [],
    chatId: null,
    pptxUrl: chunk.download_url,
    previewUrl: chunk.preview_url,
  };
  store.set(presentationAtom, skeleton);
  store.set(presentationOriginalAtom, skeleton);

  if (chunk.status === 'completed') {
    store.set(presentationPreviewAtom, {
      isOpen: true,
      presentationId: chunk.presentation_id,
    });
  }

  logger.info('[SlideBuilder] Seeded presentation atom from pptx_export', {
    presentationId: chunk.presentation_id,
  });
}
